import React, { useEffect } from 'react';
import { GetScope } from '../features/authentication/hooks/useToken';
import { useNavigate } from 'react-router-dom';
import { useClientUser } from '../providers/client.provider';
import { isAdmin } from '../util/flags';
import PropTypes from 'prop-types';
import ThinkingContainerComponent from './ui/ThinkingContainerComponent';
import useFlagsHook from '../hooks/useFlags.hook';
import { CircularProgress } from '@mui/material';
/**
 * Allows admin access to child nodes
 * @param children <ReactNode>
 */
export const AdminPermission = ({ children, redirectTo, customerId }) => {
  const hasScope = GetScope() != undefined || customerId != undefined;
  const nav = useNavigate();

  useEffect(() => {
    if (hasScope && redirectTo) {
      nav(redirectTo);
    }
  }, [redirectTo, customerId]);

  return !hasScope ? children : null;
};

AdminPermission.propTypes = {
  children: PropTypes.node.isRequired,
  redirectTo: PropTypes.string,
  customerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export const AdminOnlyPermission = ({ children, redirectTo }) => {
  const admin = isAdmin();
  const nav = useNavigate();
  useEffect(() => {
    if (!admin && redirectTo) {
      nav(redirectTo);
    }
  }, [redirectTo]);

  return admin ? children : null;
};

AdminOnlyPermission.propTypes = {
  children: PropTypes.node.isRequired,
  redirectTo: PropTypes.string,
};

const UserMatchesPermission = ({ children, ownerId, userId, redirectTo, onFail }) => {
  const nav = useNavigate();
  const hasScope = ownerId?.toString() === userId?.toString();
  useEffect(() => {
    if (!hasScope && redirectTo) {
      nav(redirectTo);
    }
  }, [redirectTo, ownerId, userId]);

  return hasScope ? children : onFail || null;
};

UserMatchesPermission.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.element, PropTypes.string]),
  ownerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  redirectTo: PropTypes.string,
  onFail: PropTypes.node,
};

export default UserMatchesPermission;

export const ENV_TESTING = 'test';
export const ENV_STAGE = 'stage';
export const ENV_PROD = 'prod';
export const ENV_DEV = 'dev';

export const FeatureAvailablePermission = ({
  children,
  featureEnabled = ENV_TESTING,
}) => {
  const dev = ['development', 'dev', 'local'];
  const stage = ['stage', 'staging'];
  const testing = ['testing', 'test', ...dev, ...stage];
  const prod = ['production', 'prod'];
  const { NODE_ENV } = process.env;
  switch (featureEnabled) {
    case ENV_TESTING:
      if (testing.includes(NODE_ENV)) {
        return children;
      }
      break;
    case ENV_STAGE:
      if (stage.includes(NODE_ENV)) {
        return children;
      }
      break;
    case ENV_PROD:
      if (prod.includes(NODE_ENV)) {
        return children;
      }
      break;
    case ENV_DEV:
      if (dev.includes(NODE_ENV)) {
        return children;
      }
      break;
  }
  return null;
};

FeatureAvailablePermission.propTypes = {
  children: PropTypes.node.isRequired,
  featureEnabled: PropTypes.oneOf([ENV_TESTING, ENV_STAGE, ENV_PROD, ENV_DEV]),
};

export const ShowOnFinishLoad = ({ children, ready, showLoader = true }) => {
  return ready ? children : !showLoader ? null : <ThinkingContainerComponent />;
};

ShowOnFinishLoad.propTypes = {
  children: PropTypes.node.isRequired,
  ready: PropTypes.bool.isRequired,
  showLoader: PropTypes.bool,
};
/**
 *
 * @param {node} children
 * @param {array} allowedTypes  Array of allowed associate types
 * @param {array} associateIds  Array of allowed associate IDs
 */
export const AllowByAssociateTypes = ({
  children,
  allowedTypes = [],
  associateIds = [],
}) => {
  const { clientUser } = useClientUser();
  const admin = isAdmin();
  let isAllowed =
    allowedTypes
      .map((str) => str.toLowerCase())
      .includes((clientUser?.userType || 'none').toLowerCase()) ||
    allowedTypes.length === 0 ||
    admin;
  if (associateIds.length > 0 && !admin) {
    isAllowed = isAllowed && associateIds.includes(clientUser?.associateId);
  }
  return isAllowed ? children : null;
};

AllowByAssociateTypes.propTypes = {
  children: PropTypes.node.isRequired,
  allowedTypes: PropTypes.arrayOf(PropTypes.string),
  associateIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
};

export const AllowByUserRole = ({ children, allowedRoles = [], redirect = '' }) => {
  const { userRole, clientUser, ready } = useClientUser();
  useEffect(() => {
    if (!allowedRoles.includes(userRole) && redirect && ready) {
      window.location = redirect.replace('{{associateId}}', clientUser?.associateId);
    }
  }, [allowedRoles, userRole, redirect, ready]);
  return allowedRoles.includes(userRole) ? children : null;
};

AllowByUserRole.propTypes = {
  children: PropTypes.node.isRequired,
  allowedRoles: PropTypes.arrayOf(PropTypes.string),
  redirect: PropTypes.string,
};
/**
 * @description Allows access if a flag value is truthy, with support for redirecting and showing a thinker while loading
 * @param {ReactNode} children
 * @param {string} flagKey  Name of the flag to check
 * @param {string} redirect Optional path to redirect if access is denied
 * @param {boolean} thinker Optional flag to show a thinker while loading
 */
export const AllowByFlagIsFilled = ({ children, flagKey, redirect, thinker = false }) => {
  const { getFlagValue, loading } = useFlagsHook();
  const nav = useNavigate();
  const flagValue = getFlagValue(flagKey);

  const allowed = Boolean(flagValue);

  useEffect(() => {
    if (!allowed && redirect && !loading) {
      nav(redirect);
    }
  }, [allowed, redirect, loading]);

  if (loading) {
    return thinker ? <ThinkingContainerComponent /> : null;
  }

  return allowed ? children : null;
};

AllowByFlagIsFilled.propTypes = {
  children: PropTypes.node.isRequired,
  flagKey: PropTypes.string.isRequired,
  redirect: PropTypes.string,
  thinker: PropTypes.bool,
};

export const AllowByFlag = ({ children, flagKey, redirect, thinker = false }) => {
  const { getFlagValue, loading } = useFlagsHook();
  const nav = useNavigate();
  const flagValue = getFlagValue(flagKey);

  const allowed = [1, '1', true, 'true'].includes(flagValue);

  useEffect(() => {
    if (!allowed && redirect && !loading) {
      nav(redirect);
    }
  }, [allowed, redirect, loading]);

  if (loading) {
    return thinker ? <ThinkingContainerComponent /> : null;
  }

  return allowed ? children : null;
};

AllowByFlag.propTypes = {
  children: PropTypes.node.isRequired,
  flagKey: PropTypes.string.isRequired,
  redirect: PropTypes.string,
  thinker: PropTypes.bool,
};
/**
 * @description Component to deny access to children based on a flag value, with support for redirecting and checking nested account values
 * @param {ReactNode} children
 * @param {string} flagName  Name of the flag to check
 * @param {string} focusValue  Value to compare against the flag (supports nested account values with 'account.' prefix)
 * @param {string} redirect Optional path to redirect if access is denied
 */
export const DenyByFlagFilter = ({ children, flagName, focusValue, redirect }) => {
  const [allow, setAllow] = React.useState(false);
  const { getFlagValue, loading } = useFlagsHook();
  const { clientUser: account, loading: accountLoading } = useClientUser();
  const nav = useNavigate();
  const flagValue = getFlagValue(flagName);
  useEffect(() => {
    if (flagValue) {
      if (!loading && !accountLoading) {
        const typeSplit = focusValue?.split('.') ?? [];
        const focusObj = typeSplit?.[0] === 'account' ? account : null;
        const fValue = focusObj?.[typeSplit?.[1]]?.toString() ?? '';
        const filterItems = flagValue.split(',').map((item) => item.trim());
        if (fValue && filterItems.includes(fValue)) {
          if (redirect) nav(redirect);
        } else {
          setAllow(true);
        }
      }
    } else {
      setAllow(true);
    }
  }, [flagValue, focusValue, loading, accountLoading, redirect]);

  return loading ? <CircularProgress /> : allow ? children : null;
};

DenyByFlagFilter.propTypes = {
  children: PropTypes.node.isRequired,
  flagName: PropTypes.string.isRequired,
  focusValue: PropTypes.string.isRequired,
  redirect: PropTypes.string,
};
